import React, { useEffect, useState } from "react";
import { Check, X, Sparkles, Wand2, Loader2, Flame } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES, ALLERGENS } from "../lib/api";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";

export default function DishConfirmCard({ parsed, transcript, onCancel, onConfirm, existingDishes = [] }) {
  const [form, setForm] = useState({
    name: parsed.dish_name || "",
    price: parsed.price ?? 0,
    category: parsed.category || "Primi",
    description: parsed.description || "",
    ingredients: parsed.ingredients || [],
    allergens: parsed.allergens || [],
    is_daily_menu: !!parsed.is_daily_menu,
  });
  const [suggesting, setSuggesting] = useState(false);

  useEffect(() => {
    setForm({
      name: parsed.dish_name || "",
      price: parsed.price ?? 0,
      category: parsed.category || "Primi",
      description: parsed.description || "",
      ingredients: parsed.ingredients || [],
      allergens: parsed.allergens || [],
      is_daily_menu: !!parsed.is_daily_menu,
    });
  }, [parsed]);

  const intent = parsed.intent || "add";
  const isUpdate = intent === "update_price" || intent === "update" || intent === "delete" || intent === "toggle_special";
  const matched = isUpdate ? existingDishes.find((d) => d.name.toLowerCase().includes((parsed.dish_name || "").toLowerCase())) : null;
  const isToggleSpecial = intent === "toggle_special";

  const suggestIngredients = async () => {
    if (!form.name.trim()) { toast.error("Inserisci prima il nome del piatto"); return; }
    setSuggesting(true);
    try {
      const { data } = await api.post("/dishes/suggest-ingredients", { name: form.name, category: form.category });
      setForm((f) => ({
        ...f,
        ingredients: Array.from(new Set([...(f.ingredients || []), ...(data.ingredients || [])])).slice(0, 12),
        allergens: Array.from(new Set([...(f.allergens || []), ...(data.allergens || [])])),
        description: f.description || data.description || "",
      }));
      toast.success(`${(data.ingredients || []).length} ingredienti suggeriti`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore AI ingredienti");
    } finally { setSuggesting(false); }
  };

  const addIngredient = () => setForm((f) => ({ ...f, ingredients: [...(f.ingredients || []), ""] }));
  const updateIngredient = (i, v) => setForm((f) => ({ ...f, ingredients: f.ingredients.map((x, idx) => idx === i ? v : x) }));
  const removeIngredient = (i) => setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, idx) => idx !== i) }));

  const toggleAllergen = (a) => {
    setForm((f) => ({
      ...f,
      allergens: f.allergens.includes(a) ? f.allergens.filter((x) => x !== a) : [...f.allergens, a],
    }));
  };

  const intentLabel = form.is_daily_menu
    ? "Aggiungere al Menu del Giorno 🍽"
    : ({
        add: "Aggiungere piatto",
        update_price: "Aggiornare prezzo",
        update: "Modificare piatto",
        delete: "Eliminare piatto",
        toggle_special: parsed.mark_as_special === false ? "Rimuovere dal Menu del Giorno" : "Aggiungere al Menu del Giorno 🔥",
        unknown: "Comando non chiaro",
      }[intent] || "Confermare azione");

  return (
    <div data-testid="voice-parse-card" className="bg-white rounded-3xl border-2 border-[#C85A32]/30 p-6 shadow-lg fade-in-up">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFF7E6] text-[#8C580C] text-xs font-semibold uppercase tracking-wider">
            <Sparkles size={12}/> AI · Confidenza {Math.round((parsed.confidence || 0.9) * 100)}%
          </div>
          <h3 className="font-serif-display text-2xl font-bold mt-3">{intentLabel}</h3>
          {transcript && <div className="mt-1 text-sm text-[#6B625B] italic">"{transcript}"</div>}
          {matched && <div className="mt-1 text-xs text-[#3B503A]">Piatto trovato: <b>{matched.name}</b></div>}
        </div>
        <button data-testid="confirm-cancel-btn" onClick={onCancel} className="w-9 h-9 rounded-full bg-[#F0EBE1] hover:bg-[#E8E2D5] grid place-items-center">
          <X size={18}/>
        </button>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Nome piatto</Label>
          <Input data-testid="confirm-name-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}/>
        </div>
        <div>
          <Label>Prezzo (€)</Label>
          <Input data-testid="confirm-price-input" type="number" step="0.50" value={form.price ?? 0} onChange={(e) => setForm({...form, price: parseFloat(e.target.value) || 0})}/>
        </div>
        <div>
          <Label>Categoria</Label>
          <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
            <SelectTrigger data-testid="confirm-category-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Descrizione</Label>
          <Input data-testid="confirm-desc-input" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="opzionale"/>
        </div>
      </div>

      <div className="mt-5">
        <Label className="mb-2 block">Allergeni contenuti</Label>
        <div className="flex flex-wrap gap-2">
          {ALLERGENS.map((a) => {
            const active = form.allergens.includes(a);
            return (
              <button
                key={a}
                data-testid={`confirm-allergen-${a}`}
                onClick={() => toggleAllergen(a)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                  active ? "bg-[#FEF3D6] text-[#8C580C] border-[#F4E3B8]" : "bg-white text-[#6B625B] border-[#E8E2D5]"
                }`}
              >
                {a}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <Label>Ingredienti</Label>
          <button
            data-testid="suggest-ingredients-btn"
            onClick={suggestIngredients}
            disabled={suggesting || !form.name.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#D99B26] to-[#C85A32] text-white text-xs font-bold disabled:opacity-50 shadow"
          >
            {suggesting ? <Loader2 size={12} className="animate-spin"/> : <Wand2 size={12}/>}
            {suggesting ? "AI..." : "Suggerisci con AI"}
          </button>
        </div>
        {form.ingredients.length === 0 ? (
          <div className="text-xs text-[#6B625B] italic bg-[#FBF9F5] rounded-xl border border-dashed border-[#E8E2D5] p-3">Nessun ingrediente. Clicca "Suggerisci con AI" o aggiungi manualmente.</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {form.ingredients.map((ing, i) => (
              <div key={i} className="inline-flex items-center gap-1 bg-[#EAF0E9] text-[#3B503A] rounded-full pl-3 pr-1 py-0.5 text-xs">
                <input value={ing} onChange={(e) => updateIngredient(i, e.target.value)} className="bg-transparent outline-none min-w-[60px]" style={{ width: `${Math.max(ing.length, 4) * 7}px` }}/>
                <button onClick={() => removeIngredient(i)} className="w-5 h-5 rounded-full bg-[#3B503A]/20 hover:bg-[#3B503A]/40 grid place-items-center"><X size={9}/></button>
              </div>
            ))}
          </div>
        )}
        <button data-testid="add-ingredient-btn" onClick={addIngredient} className="mt-2 text-xs text-[#6B625B] font-semibold underline">+ Aggiungi ingrediente</button>
      </div>

      <div className="mt-6 flex flex-wrap gap-3 justify-end">
        <button data-testid="confirm-discard-btn" onClick={onCancel} className="px-5 py-2.5 rounded-full border border-[#E8E2D5] text-[#231F1D] font-semibold hover:bg-[#F0EBE1]">
          Annulla
        </button>
        <button
          data-testid="confirm-add-dish-btn"
          onClick={() => onConfirm({ ...form, ingredients: form.ingredients.filter((x) => x && x.trim()), matched, intent })}
          className="px-5 py-2.5 rounded-full bg-[#3B503A] hover:bg-[#2f4030] text-white font-semibold inline-flex items-center gap-2"
        >
          <Check size={16}/> Conferma e Salva
        </button>
      </div>
    </div>
  );
}
